import { jsPDF } from "jspdf";
import { LocalStorageKeys } from "@/components/pages/login/hooks/useLocalStorage";
import { Room } from "@/types/room";

const MOBILE_APP_URL = process.env.MOBILE_APP_URL || "";

export const getRoomDisplayName = (room: Pick<Room, "number">) =>
  `Room ${room.number}`;

export const getRoomQrFileName = (room: Pick<Room, "number">) =>
  `room-${room.number}-qr-sticker`;

export const buildRoomQrUrl = ({
  hotelId,
  roomId,
  tenantId,
}: {
  hotelId: string;
  roomId: string;
  tenantId: string;
}) => {
  if (!MOBILE_APP_URL.trim()) {
    throw new Error("MOBILE_APP_URL is not configured");
  }

  const url = new URL("/app/Room", MOBILE_APP_URL);
  url.searchParams.set("fromQr", "true");
  url.searchParams.set("tenantId", tenantId);
  url.searchParams.set("hotelId", hotelId);
  url.searchParams.set("roomId", roomId);

  return url.toString();
};

export const getTenantIdFromStorage = () =>
  localStorage.getItem(LocalStorageKeys.TENANT_ID);

export const downloadRoomQrPdf = async ({
  title,
  qrDataUrl,
  fileName,
}: {
  title: string;
  qrDataUrl: string;
  fileName: string;
}) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const qrSize = 220;
  const titleY = 84;
  const qrX = (pageWidth - qrSize) / 2;
  const qrY = 120;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(title, pageWidth / 2, titleY, { align: "center" });
  doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
  doc.save(`${fileName}.pdf`);
};

export const printRoomQrSticker = ({
  title,
  qrDataUrl,
}: {
  title: string;
  qrDataUrl: string;
}) => {
  const printWindow = window.open("", "_blank", "width=700,height=900");

  if (!printWindow) {
    throw new Error("Unable to open print window");
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <title>${title}</title>
        <style>
          @page {
            size: auto;
            margin: 16mm;
          }

          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
            font-family: Arial, sans-serif;
            color: #111827;
            background: #ffffff;
          }

          .sticker {
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 24px;
            padding: 32px 24px;
          }

          .title {
            font-size: 28px;
            font-weight: 700;
            text-align: center;
          }

          .qr {
            width: 320px;
            height: 320px;
            object-fit: contain;
          }
        </style>
      </head>
      <body>
        <div class="sticker">
          <div class="title">${title}</div>
          <img class="qr" src="${qrDataUrl}" alt="${title}" />
        </div>
      </body>
    </html>
  `);

  printWindow.document.close();

  const triggerPrint = () => {
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const waitForQrImage = () => {
    const qrImage = printWindow.document.querySelector<HTMLImageElement>(".qr");

    if (!qrImage) {
      window.setTimeout(triggerPrint, 50);
      return;
    }

    if (qrImage.complete) {
      window.setTimeout(triggerPrint, 50);
      return;
    }

    qrImage.addEventListener(
      "load",
      () => window.setTimeout(triggerPrint, 50),
      {
        once: true,
      },
    );
    qrImage.addEventListener(
      "error",
      () => window.setTimeout(triggerPrint, 50),
      {
        once: true,
      },
    );
  };

  if (printWindow.document.readyState === "complete") {
    waitForQrImage();
    return;
  }

  printWindow.addEventListener("load", waitForQrImage, { once: true });
};
